import { useEffect, useState } from "react";
import { ownerApi, formatDetail } from "../api";
import { Loader2, Palette } from "lucide-react";
import { toast } from "@/lib/toastBanner";

export default function Themes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await ownerApi.get("/themes");
        if (!cancelled) setData(data);
      } catch (e) {
        toast.error(formatDetail(e) || "Failed to load themes");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#10b7c0]" />
      </div>
    );
  }

  const themes = data?.themes || [];
  const totalOrgs = data?.total_orgs || 0;

  return (
    <div className="space-y-6" data-testid="owner-themes-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#f1fcff] flex items-center gap-2">
            <Palette className="h-6 w-6 text-[#10b7c0]" />
            Themes
          </h1>
          <p className="text-sm text-[#ebeef5]/70 mt-1 max-w-2xl">
            Locked catalog of 10 brand palettes. System Admins pick one from their Global Settings; it paints the sidebar and top-nav for every role in the org. Status badges (Published / Draft / Error) stay homogenous regardless of theme.
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-[#ebeef5]/50">Orgs</p>
          <p className="font-heading text-2xl font-bold text-[#f1fcff]" data-testid="owner-themes-total-orgs">{totalOrgs}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {themes.map((t) => (
          <div
            key={t.id}
            className="rounded-xl overflow-hidden border border-[#10b7c0]/20 bg-[#0a1530] hover:border-[#10b7c0]/40 transition"
            data-testid={`owner-theme-card-${t.id}`}
          >
            <div className="h-20 flex" style={{ backgroundColor: t.primary }}>
              <div className="w-1/3 h-full" style={{ backgroundColor: t.secondary }} />
            </div>
            <div className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-[#f1fcff]">{t.name}</h3>
                <span className="text-xs font-body px-2 py-0.5 rounded bg-[#10b7c0]/15 text-[#3fcdd4]" data-testid={`owner-theme-count-${t.id}`}>
                  {t.org_count} {t.org_count === 1 ? "org" : "orgs"}
                </span>
              </div>
              <div className="flex gap-3 text-[10px] font-mono text-[#ebeef5]/60">
                <span>primary {t.primary}</span>
                <span>secondary {t.secondary}</span>
              </div>
              {t.orgs.length > 0 && (
                <div className="pt-2 border-t border-[#10b7c0]/10 space-y-1" data-testid={`owner-theme-orgs-${t.id}`}>
                  {t.orgs.slice(0, 5).map((o) => (
                    <p key={o.id} className="text-xs text-[#ebeef5]/80 truncate">
                      · {o.name} <span className="text-[#ebeef5]/40">({o.slug})</span>
                    </p>
                  ))}
                  {t.orgs.length > 5 && (
                    <p className="text-xs text-[#ebeef5]/40">+ {t.orgs.length - 5} more</p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
